import { useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Chip,
  Divider,
  Stack,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DescriptionIcon from '@mui/icons-material/Description';
import { useLanguage } from '../../../../context/LanguageContext';
import { useLayoutPreferences } from '../../../../context/LayoutPreferencesContext';
import { DefinitionFormData } from './CommunicationDefinitionStep';
import { TargetingFormData } from './CommunicationTargetingStep';
import { OfferFormData } from './CommunicationOffersStep';

const formatDate = (iso: string) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

const formatPeriod = (start: string, end: string) => {
  if (!start && !end) return null;
  return `Du ${formatDate(start)} au ${formatDate(end)}`;
};

interface CommunicationValidationStepProps {
  definition: DefinitionFormData;
  targeting: TargetingFormData;
  offers: OfferFormData[];
  hasCreation: boolean;
  onBatAcknowledged?: (ack: boolean) => void;
  onPreviewBat?: () => void;
  previewingBat?: boolean;
  onDownloadDevis?: () => void;
  downloadingDevis?: boolean;
}

function CommunicationValidationStep({
  definition, targeting, offers, hasCreation, onBatAcknowledged, onPreviewBat, previewingBat = false,
  onDownloadDevis, downloadingDevis = false,
}: CommunicationValidationStepProps) {
  const { t } = useLanguage();
  const { sidebarColor } = useLayoutPreferences();
  const [checkedBat, setCheckedBat] = useState(false);

  const visibleOffers = offers.filter(o => o.title.trim() !== '');

  const primaryBtn = {
    backgroundColor: sidebarColor,
    '&:hover': { backgroundColor: sidebarColor, opacity: 0.9 },
  };

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
        {t('pages.marketing.communication.validation')}
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>
            {t('pages.marketing.communication.communicationSummary')}
          </Typography>

          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="body2" fontWeight={700}>{definition.title || '—'}</Typography>
              {definition.sendDate && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Envoi programmé au {formatDate(definition.sendDate)}
                </Typography>
              )}
              <Chip label="Brouillon" size="small" sx={{ mb: 2, fontSize: '0.7rem' }} />

              <Grid container spacing={2} sx={{ mb: 1 }}>
                <Grid size={4}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    {t('pages.marketing.communication.sendByCustomer')}
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>{definition.sendByCustomer ? 'OUI' : 'NON'}</Typography>
                </Grid>
                <Grid size={4}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    Template associé
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>{hasCreation ? 'OUI' : 'NON'}</Typography>
                </Grid>
                <Grid size={4}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    Nb. de destinataires
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {targeting.nbRecipients !== null ? targeting.nbRecipients.toLocaleString('fr-FR') : '—'}
                  </Typography>
                </Grid>
              </Grid>

              {visibleOffers.length > 0 && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 1 }}>
                    Offre(s)
                  </Typography>
                  <Stack spacing={1}>
                    {visibleOffers.map(offer => {
                      const period = formatPeriod(offer.startValidityDate, offer.endValidityDate);

                      let discountLabel: string | null = null;
                      const dtype = offer.discountTypeIds[0];
                      if (dtype === 2) {
                        if (offer.pointsValue) {
                          discountLabel = offer.pointsMode === 'multiplication'
                            ? `× ${offer.pointsValue} pts`
                            : `+ ${offer.pointsValue} pts`;
                        }
                      } else if (dtype === 1 || dtype === 3) {
                        if (offer.remiseValue) {
                          discountLabel = offer.remiseMode === 'pourcentage'
                            ? `Remise ${offer.remiseValue} %`
                            : `Remise ${offer.remiseValue} €`;
                        }
                      }

                      return (
                        <Box
                          key={offer.id}
                          sx={{
                            border: '1px solid #e0e0e0',
                            borderRadius: 1.5,
                            p: 1.5,
                            backgroundColor: '#fafafa',
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                            <Typography variant="body2" fontWeight={700} sx={{ flex: 1 }}>
                              {offer.title}
                            </Typography>
                            {offer.offerNumber && (
                              <Chip
                                label={offer.offerNumber}
                                size="small"
                                sx={{ fontSize: '0.65rem', height: 18, ml: 1, fontFamily: 'monospace', bgcolor: '#e3f2fd', color: '#1565c0' }}
                              />
                            )}
                          </Box>
                          {offer.description && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                              {offer.description}
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.5 }}>
                            {discountLabel && (
                              <Chip label={discountLabel} size="small" sx={{ fontSize: '0.68rem', height: 20, bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                            )}
                            {period && (
                              <Chip label={period} size="small" sx={{ fontSize: '0.68rem', height: 20, bgcolor: '#f3e5f5', color: '#6a1b9a' }} />
                            )}
                            {offer.nbMaxEdit && (
                              <Chip label={`Max ${offer.nbMaxEdit} éd.`} size="small" sx={{ fontSize: '0.68rem', height: 20, bgcolor: '#fff3e0', color: '#e65100' }} />
                            )}
                            {offer.nbMaxUse && (
                              <Chip label={`Max ${offer.nbMaxUse} util.`} size="small" sx={{ fontSize: '0.68rem', height: 20, bgcolor: '#fce4ec', color: '#880e4f' }} />
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Card variant="outlined" sx={{ borderRadius: 2, backgroundColor: '#f8f9fa' }}>
            <CardContent>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                {t('pages.marketing.communication.previewBat')}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Visualisez le BAT de votre communication avant de valider.
              </Typography>
              <Tooltip
                title={!hasCreation ? 'Veuillez d\'abord compléter l\'étape Création' : ''}
                disableHoverListener={hasCreation}
              >
                <span>
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    startIcon={previewingBat ? <CircularProgress size={18} color="inherit" /> : <VisibilityIcon />}
                    disabled={!hasCreation || previewingBat}
                    onClick={onPreviewBat}
                    sx={hasCreation ? primaryBtn : {}}
                  >
                    {previewingBat ? 'GÉNÉRATION EN COURS…' : 'PRÉVISUALISER LE BAT'}
                  </Button>
                </span>
              </Tooltip>

              <Button
                variant="outlined"
                fullWidth
                size="large"
                startIcon={downloadingDevis ? <CircularProgress size={18} color="inherit" /> : <DescriptionIcon />}
                disabled={downloadingDevis}
                onClick={onDownloadDevis}
                sx={{ mt: 1.5, textTransform: 'none', fontWeight: 600, color: sidebarColor, borderColor: sidebarColor }}
              >
                {downloadingDevis ? 'GÉNÉRATION EN COURS…' : 'VOIR LE DEVIS'}
              </Button>

              <Divider sx={{ my: 2 }} />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={checkedBat}
                    onChange={e => {
                      setCheckedBat(e.target.checked);
                      onBatAcknowledged?.(e.target.checked);
                    }}
                    sx={{ '&.Mui-checked': { color: sidebarColor } }}
                  />
                }
                label={
                  <Typography variant="caption">
                    J'ai pris connaissance du BAT de ma communication.
                  </Typography>
                }
                sx={{ display: 'flex', alignItems: 'center' }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default CommunicationValidationStep;
